import { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { withErrorBoundary } from '@/components/ErrorBounddary'
import Button from '../Button'

type CycleTypewriterProps = {
  texts: string[]
  speed?: number
  pauseDuration?: number
  className?: string
}

const CycleTypewriter = ({ texts, speed = 150, pauseDuration = 2000, className = '' }: CycleTypewriterProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0)
  const [displayText, setDisplayText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const stableTexts = useMemo(() => texts, [texts])

  useEffect(() => {
    if (!stableTexts.length) return undefined

    const currentText = stableTexts[currentTextIndex % stableTexts.length]
    const isPaused = !isDeleting && displayText.length >= currentText.length

    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          if (displayText.length < currentText.length) {
            setDisplayText(currentText.slice(0, displayText.length + 1))
          } else {
            setIsDeleting(true)
          }
        } else if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1))
        } else {
          setIsDeleting(false)
          setCurrentTextIndex((prev) => prev + 1)
        }
      },
      isPaused ? pauseDuration : isDeleting ? speed / 2 : speed
    )

    return () => clearTimeout(timeout)
  }, [displayText, isDeleting, currentTextIndex, stableTexts, speed, pauseDuration])

  if (!stableTexts.length) {
    return null
  }

  return (
    <div className={className}>
      {displayText}
      <span className="animate-pulse text-white">|</span>
    </div>
  )
}

const HERO_TYPING_TEXTS = ['棋牌 / 球类赛事系统', '球拍类小组淘汰赛', 'AI 数据分析', '实时对战平台', '普通组队赛完全免费']

const FirstPage = ({ isLogin }) => {
  const router = useRouter()

  return (
    <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/5 px-6 py-6 shadow-[0_12px_35px_rgba(3,7,18,0.35)] backdrop-blur-sm sm:px-8 sm:py-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,153,102,0.2),transparent_55%),radial-gradient(circle_at_80%_25%,rgba(83,141,255,0.24),transparent_45%),radial-gradient(circle_at_50%_80%,rgba(126,255,173,0.2),transparent_50%)]" />
        <div className="absolute inset-x-10 top-12 h-72 rounded-full bg-gradient-to-r from-orange-500/15 via-transparent to-sky-500/15" />
        <div className="bg-orange-500/10 absolute -left-32 top-0 h-96 w-96 rounded-full" />
        <div className="bg-sky-500/10 absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full" />
        <div className="absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/5 opacity-30 blur-[2px]" />
      </div>

      <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-center lg:gap-8">
        <div className="col-span-1 flex items-center justify-center lg:col-span-4">
          <div className="relative w-full max-w-[260px] lg:max-w-none">
            <div className="absolute inset-4 -z-10 rounded-[28px] border border-white/10 bg-gradient-to-br from-white/10 to-transparent blur-xl" />
            <Image
              src="/lingti-charactor.png"
              alt="灵缇赛事系统"
              width={716}
              height={716}
              priority
              className="z-10 h-auto w-full object-contain drop-shadow-[0_20px_50px_rgba(4,6,15,0.6)]"
            />
          </div>
        </div>

        <div className="col-span-1 px-2 lg:col-span-8 lg:px-0">
          <div className="space-y-4 lg:space-y-5">
            <div className="space-y-3 lg:space-y-4">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.35em] text-white/80">
                AI ERA
                <span className="rounded-full bg-gradient-to-r from-orange-500/90 to-amber-400/80 px-2.5 py-0.5 text-[10px] tracking-[0.2em] text-white">全新体验</span>
              </div>
              <h1 className="text-2xl font-bold leading-tight text-white sm:text-3xl lg:text-4xl xl:text-5xl">
                <span className="bg-gradient-to-r from-white via-orange-200 to-orange-300 bg-clip-text text-transparent">灵缇互娱</span>
                <span className="ml-3 text-base font-medium text-white/70 sm:text-lg lg:text-xl">AI+体育综合服务商 · 软硬件体育装备制造商</span>
              </h1>
              <CycleTypewriter texts={HERO_TYPING_TEXTS} speed={90} pauseDuration={2500} className="text-base font-semibold tracking-wide text-white sm:text-xl lg:text-2xl" />
              <p className="max-w-2xl text-sm text-white/80">
                <span className="font-semibold text-amber-200">灵缇赛事系统</span> 是灵缇互娱旗下产品，融合人工智能与电竞技术，覆盖棋牌（掼蛋 / 桥牌 / 升级）与球类（匹克球 / 网球 / 羽毛球 / 乒乓球）赛事，为智力与竞技运动提供全方位数字化服务支持。
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {['棋牌 / 球类赛事系统', '球拍类小组淘汰赛', 'AI 数据分析', '实时对战平台', '专业裁判支持'].map((tag) => (
                <div
                  key={tag}
                  className="rounded-full border border-white/10 bg-gradient-to-r from-white/10 to-white/0 px-3 py-1 text-xs font-medium text-white/90 lg:text-sm"
                >
                  {tag}
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4">
              <Button
                type="primary"
                onClick={() => router.push(isLogin ? '/match?is_create=1' : '/login?redirect=match?is_create=1')}
                className="group relative overflow-hidden rounded-full px-7 py-3 text-base font-semibold shadow-[0_12px_30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:scale-105 hover:shadow-[0_22px_48px_rgba(248,113,113,0.55)] lg:px-9 lg:py-4 lg:text-lg"
              >
                <span className="relative z-10">立即办赛</span>
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-rose-500 to-red-600" />
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-rose-600 to-red-700 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </Button>
              <Button
                type="link"
                onClick={() => router.push('/contact')}
                className="group relative rounded-full border border-white/20 bg-gradient-to-r from-white/10 to-white/5 px-7 py-3 text-base font-semibold shadow-[0_12px_30px_rgba(15,23,42,0.35)] transition-all duration-300 hover:scale-105 hover:border-orange-400/50 lg:px-9 lg:py-4 lg:text-lg"
              >
                <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent group-hover:from-orange-300 group-hover:to-orange-200">联系我们</span>
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-3 sm:gap-4">
              {[
                { value: '10000+', label: '举办赛事' },
                { value: '99%', label: '好评率' },
                { value: '7×24H', label: '技术支持' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
                  <div className="bg-gradient-to-r from-white to-orange-200 bg-clip-text text-lg font-bold text-transparent sm:text-xl lg:text-2xl">{stat.value}</div>
                  <div className="text-xs text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default withErrorBoundary(FirstPage)
